import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import logo from '../traderhub24-logo.svg'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'
import Login from './components/Login'
import Register from './components/Register'

type PageKey =
  | 'home'
  | 'changelog'
  | 'privacy-policy'
  | 'terms-and-conditions'
  | 'risk-warning'
  | 'documentation'
  | 'all-trades'
  | 'analytics'
  | 'tools'
  | 'strategies'
  | 'account'
  | 'symbol-management'
  | 'social-trade'
  | 'social-trade-trade-pools'
  | 'social-trade-followers'

type FollowerRow = {
  id: string
  email: string
  scope: 'global' | 'pool'
  trade_pool_id: string | null
  created_at: string
}

type PoolRow = {
  id: string
  name: string
}

type FollowerAccess = {
  email: string
  globalId: string | null
  poolAccesses: Array<{ id: string; poolId: string; poolName: string }>
}

function normalizeEmail(s: string) {
  return s.trim().toLowerCase()
}

function isValidEmail(email: string) {
  // pragmatic validation for UI; DB does normalization + uniqueness
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function parseEmails(input: string) {
  // split on newline/comma/semicolon/space/tab
  const parts = input
    .split(/[\s,;]+/g)
    .map((x) => normalizeEmail(x))
    .filter(Boolean)

  const unique: string[] = []
  const seen = new Set<string>()
  for (const e of parts) {
    if (!seen.has(e)) {
      seen.add(e)
      unique.push(e)
    }
  }
  return unique
}

function Followers({
  onBack,
  setCurrentPage,
  embedded,
}: {
  onBack: () => void
  setCurrentPage: (page: PageKey) => void
  embedded?: boolean
}) {
  const navigate = useNavigate()
  const { user, signOut, loading: authLoading } = useAuth()
  const { isFree, profileLoading, canPublishTrades, canCreatePools } = useMembership()
  const { showError, showInfo, showConfirm } = useNotification()
  const canManageFollowers = canPublishTrades || canCreatePools


  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [followers, setFollowers] = useState<FollowerRow[]>([])
  const [pools, setPools] = useState<PoolRow[]>([])
  const [search, setSearch] = useState('')

  const [email, setEmail] = useState('')
  const [bulk, setBulk] = useState('')
  const [importing, setImporting] = useState(false)
  const [editingFollowerEmail, setEditingFollowerEmail] = useState<string | null>(null)

  const stats = useMemo(() => {
    const parsed = parseEmails(bulk)
    const valid = parsed.filter(isValidEmail)
    return { parsedCount: parsed.length, validCount: valid.length }
  }, [bulk])

  // Group followers by email and build access structure
  const followerAccesses = useMemo(() => {
    const accessMap = new Map<string, FollowerAccess>()

    for (const f of followers) {
      const email = f.email.toLowerCase()
      if (!accessMap.has(email)) {
        accessMap.set(email, {
          email: f.email,
          globalId: null,
          poolAccesses: [],
        })
      }

      const access = accessMap.get(email)!
      if (f.scope === 'global') {
        access.globalId = f.id
      } else if (f.scope === 'pool' && f.trade_pool_id) {
        const pool = pools.find((p) => p.id === f.trade_pool_id)
        if (pool) {
          access.poolAccesses.push({
            id: f.id,
            poolId: f.trade_pool_id,
            poolName: pool.name,
          })
        }
      }
    }

    return Array.from(accessMap.values())
  }, [followers, pools])

  const filteredFollowerAccesses = useMemo(() => {
    const s = search.trim().toLowerCase()
    if (!s) return followerAccesses
    return followerAccesses.filter((fa) => fa.email.toLowerCase().includes(s))
  }, [followerAccesses, search])

  // Session control - redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) setShowLogin(true)
  }, [authLoading, user])

  const fetchPools = async () => {
    if (!user) return
    try {
      const { data, error: qError } = await supabase
        .from('trade_pools')
        .select('id, name')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      if (qError) throw qError
      setPools((data as PoolRow[]) || [])
    } catch (err: any) {
      console.warn('Failed to load pools', err)
    }
  }

  const fetchFollowers = async () => {
    if (!user) return
    try {
      setLoading(true)
      // Fetch all followers (global + pool)
      const { data, error: qError } = await supabase
        .from('followers')
        .select('id, email, scope, trade_pool_id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (qError) throw qError
      setFollowers((data as FollowerRow[]) || [])
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load followers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchFollowers()
      fetchPools()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const handleUpgrade = () => {
    setCurrentPage('home')
    setTimeout(() => {
      const el = document.getElementById('pricing')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const getAutoAddPoolIds = async () => {
    if (!user) return []
    const { data, error: qError } = await supabase.from('trade_pools').select('id, settings').eq('user_id', user.id)
    if (qError) throw qError
    const rows = (data as any[]) || []
    return rows.filter((r) => Boolean(r?.settings?.addNewFollowersAutomatically)).map((r) => r.id as string)
  }

  const addEmailsToAutoAddPools = async (emails: string[]) => {
    if (!user) return
    if (emails.length === 0) return

    let poolIds: string[] = []
    try {
      poolIds = await getAutoAddPoolIds()
    } catch (err: any) {
      // If this fails, we still want the global follower add/import to succeed.
      console.warn('Failed to load trade pools for auto-add', err)
      return
    }

    if (poolIds.length === 0) return

    for (const poolId of poolIds) {
      for (const email of emails) {
        const { error: iError } = await supabase.from('followers').insert({
          user_id: user.id,
          email,
          scope: 'pool',
          trade_pool_id: poolId,
        })
        if (iError) {
          if ((iError as any).code === '23505') continue
          // Ignore other errors per-item so one bad insert doesn't block the rest.
          console.warn('Auto-add follower insert failed', { poolId, email, iError })
        }
      }
    }
  }

  const addOne = async () => {
    if (!user) return
    const e = normalizeEmail(email)
    if (!e) return
    if (!isValidEmail(e)) {
      showError('Invalid email')
      return
    }

    try {
      const { error: iError } = await supabase.from('followers').insert({
        user_id: user.id,
        email: e,
        scope: 'global',
        trade_pool_id: null,
      })
      if (iError) {
        // 23505 unique violation (duplicate) - ignore
        // supabase-js error shape can vary; be defensive
        if ((iError as any).code === '23505') {
          // Still auto-add to pools that have it enabled
          await addEmailsToAutoAddPools([e])
          setEmail('')
          return
        }
        throw iError
      }

      await addEmailsToAutoAddPools([e])
      setEmail('')
      await fetchFollowers()
    } catch (err: any) {
      showError(err.message || 'Failed to add follower')
    }
  }

  const importBulk = async () => {
    if (!user) return
    const parsed = parseEmails(bulk)
    if (parsed.length === 0) return

    const valid = parsed.filter(isValidEmail)
    const invalid = parsed.filter((x) => !isValidEmail(x))

    setImporting(true)
    let added = 0
    let duplicates = 0
    let failed = 0

    try {
      // Insert sequentially to avoid one duplicate failing whole batch.
      for (const e of valid) {
        const { error: iError } = await supabase.from('followers').insert({
          user_id: user.id,
          email: e,
          scope: 'global',
          trade_pool_id: null,
        })
        if (iError) {
          if ((iError as any).code === '23505') {
            duplicates += 1
            continue
          }
          failed += 1
          continue
        }
        added += 1
      }

      // Auto-add (attempt for all valid emails; duplicates are ignored by unique constraint)
      await addEmailsToAutoAddPools(valid)

      await fetchFollowers()
      setBulk('')

      const parts: string[] = []
      if (added) parts.push(`${added} added`)
      if (duplicates) parts.push(`${duplicates} duplicates skipped`)
      if (invalid.length) parts.push(`${invalid.length} invalid skipped`)
      if (failed) parts.push(`${failed} failed`)
      showInfo(parts.length ? parts.join(', ') : 'Nothing imported')
    } finally {
      setImporting(false)
    }
  }

  const toggleGlobalAccess = async (email: string, hasGlobal: boolean) => {
    if (!user) return
    try {
      if (hasGlobal) {
        // Remove global access
        const access = followerAccesses.find((fa) => fa.email.toLowerCase() === email.toLowerCase())
        if (access?.globalId) {
          const { error: dError } = await supabase.from('followers').delete().eq('id', access.globalId)
          if (dError) throw dError
        }
      } else {
        // Add global access
        const { error: iError } = await supabase.from('followers').insert({
          user_id: user.id,
          email: normalizeEmail(email),
          scope: 'global',
          trade_pool_id: null,
        })
        if (iError && (iError as any).code !== '23505') throw iError
      }
      await fetchFollowers()
    } catch (err: any) {
      showError(err.message || 'Failed to update global access')
    }
  }

  const togglePoolAccess = async (email: string, poolId: string, hasAccess: boolean) => {
    if (!user) return
    try {
      if (hasAccess) {
        // Remove pool access
        const access = followerAccesses.find((fa) => fa.email.toLowerCase() === email.toLowerCase())
        const poolAccess = access?.poolAccesses.find((pa) => pa.poolId === poolId)
        if (poolAccess) {
          const { error: dError } = await supabase.from('followers').delete().eq('id', poolAccess.id)
          if (dError) throw dError
        }
      } else {
        // Add pool access
        const { error: iError } = await supabase.from('followers').insert({
          user_id: user.id,
          email: normalizeEmail(email),
          scope: 'pool',
          trade_pool_id: poolId,
        })
        if (iError) {
          if ((iError as any).code === '23505') {
            showInfo('Follower already has access to this pool')
            return
          }
          throw iError
        }
      }
      await fetchFollowers()
    } catch (err: any) {
      showError(err.message || 'Failed to update pool access')
    }
  }

  const removeFollower = async (email: string) => {
    const confirmed = await showConfirm({
      title: 'Remove Follower',
      message: `Remove ${email}? This will remove all access (global and pool access).`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmButtonStyle: 'danger'
    })
    if (!confirmed) return
    try {
      const { error: dError } = await supabase
        .from('followers')
        .delete()
        .eq('user_id', user!.id)
        .eq('email', normalizeEmail(email))
      if (dError) throw dError
      await fetchFollowers()
    } catch (err: any) {
      showError(err.message || 'Failed to remove follower')
    }
  }

  // If not authenticated, show login prompt (skip when embedded; layout handles auth)
  if (!user && !authLoading && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        <header className="w-full flex justify-between items-center py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <img
              src={logo}
              alt="TraderHub24"
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onBack}
            />
          </div>
          <div className="flex items-center gap-2" />
        </header>

        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center mb-4">Please sign in to access Followers</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowLogin(true)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowRegister(true)}
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>

        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false)
              setShowRegister(true)
            }}
          />
        )}
        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false)
              setShowLogin(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img
            src={logo}
            alt="TraderHub24"
            className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/app/social-trade")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              Back to Social Trade
            </button>
            <button
              type="button"
              onClick={() => signOut()}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>
      )}

      {(() => {
        const W = embedded ? "div" : "main";
        return (
        <W className={embedded ? "w-full px-6 py-6" : "max-w-6xl mx-auto px-6 py-6"}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Followers</h1>
          <p className="text-gray-600 mt-2">Manage the people who follow you (email list).</p>
        </div>

        {!canManageFollowers && !profileLoading && (
          <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-lg text-sm">
              {isFree ? 'Bu özelliği aktifleştirmek için Pro abone olun.' : 'Bu özellik üyeliğiniz için kapalı.'}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleUpgrade}
                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
              >
                Planları gör
              </button>
              <button
                type="button"
                onClick={() => navigate("/app/social-trade")}
                className="px-5 py-2 bg-app-box border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Social Trade’e dön
              </button>
            </div>
          </div>
        )}

        {canManageFollowers && (
          <div className="space-y-6">
            <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900">Add follower</h2>
              <div className="mt-4 flex gap-2">
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={addOne}
                  disabled={!email.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-900">Bulk import</h3>
                <p className="text-xs text-gray-500 mt-1">Paste emails separated by commas, spaces, or new lines.</p>
                <textarea
                  value={bulk}
                  onChange={(e) => setBulk(e.target.value)}
                  rows={6}
                  placeholder={`alice@example.com\nbob@example.com\n...`}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-gray-500">
                    Parsed: <span className="font-semibold">{stats.parsedCount}</span> • Valid:{' '}
                    <span className="font-semibold">{stats.validCount}</span>
                  </div>
                  <button
                    type="button"
                    onClick={importBulk}
                    disabled={importing || stats.parsedCount === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </div>
            </section>

            <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Your followers</h2>
                <div className="flex items-center gap-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search email..."
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    type="button"
                    onClick={fetchFollowers}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {loading ? (
                <p className="text-gray-500 text-center py-10">Loading followers...</p>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-4">{error}</div>
              ) : followerAccesses.length === 0 ? (
                <p className="text-gray-600 mt-4">No followers yet.</p>
              ) : filteredFollowerAccesses.length === 0 ? (
                <p className="text-gray-600 mt-4">No followers match your search.</p>
              ) : (
                <ul className="mt-4 divide-y divide-gray-200 bg-white rounded-xl border border-gray-200">
                  {filteredFollowerAccesses.map((access) => (
                    <li key={access.email} className="px-4 py-3 flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-800">{access.email}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingFollowerEmail(access.email)}
                          className="px-3 py-1.5 text-xs font-medium text-orange-600 bg-white border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
                        >
                          Manage
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFollower(access.email)}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}

        {/* Follower Access Management Modal */}
        {editingFollowerEmail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Manage Access</h3>
                  <button
                    type="button"
                    onClick={() => setEditingFollowerEmail(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-1">Follower</div>
                  <div className="text-sm text-gray-900">{editingFollowerEmail}</div>
                </div>

                <div className="space-y-4">
                  {/* Global Access */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-center gap-3 select-none cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-5 w-5 accent-orange-500"
                        checked={followerAccesses.find((fa) => fa.email.toLowerCase() === editingFollowerEmail.toLowerCase())?.globalId !== null}
                        onChange={(e) => {
                          const access = followerAccesses.find((fa) => fa.email.toLowerCase() === editingFollowerEmail.toLowerCase())
                          toggleGlobalAccess(editingFollowerEmail, access?.globalId !== null)
                        }}
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Global Access</div>
                        <div className="text-xs text-gray-500 mt-1">Can view trades without a pool assignment</div>
                      </div>
                    </label>
                  </div>

                  {/* Pool Access */}
                  {pools.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-3">Pool Access</div>
                      <div className="space-y-2">
                        {pools.map((pool) => {
                          const access = followerAccesses.find((fa) => fa.email.toLowerCase() === editingFollowerEmail.toLowerCase())
                          const hasPoolAccess = access?.poolAccesses.some((pa) => pa.poolId === pool.id) || false

                          return (
                            <label key={pool.id} className="flex items-center gap-3 select-none cursor-pointer bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                              <input
                                type="checkbox"
                                className="h-5 w-5 accent-orange-500"
                                checked={hasPoolAccess}
                                onChange={(e) => togglePoolAccess(editingFollowerEmail, pool.id, hasPoolAccess)}
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{pool.name}</div>
                                <div className="text-xs text-gray-500 mt-1">Can view trades in this pool</div>
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {pools.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-4">No pools available. Create pools in Trade Pools page.</div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingFollowerEmail(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </W>
        );
      })()}
    </div>
  )
}

export default Followers

